/**
 * Utility functions for creating Google Maps marker icons
 */
import { escapeHtml } from './htmlEscape';

// Dispatcher "needs attention" flag: a red ring drawn OUTSIDE each marker's
// existing white/gray stroke. The stroke insulates the red from the fill (so it
// never blends into a driver's box color), and the ring enlarges the marker so
// flagged orders also read bigger. Driven by order.dispatch_note being non-empty.
const FLAG_COLOR = '#dc2626'; // red-600
const FLAG_RING = 3; // px the red ring extends beyond the marker on every side

/**
 * Wrap a marker's inner SVG shapes, optionally adding the red flag ring.
 * Inner shapes are drawn in the base (unflagged) coordinate space and shifted
 * outward when flagged, so callers never have to change their own coordinates.
 *
 * @param {string} inner - SVG shapes in the base viewBox coordinate space
 * @param {number} baseW - base viewBox width
 * @param {number} baseH - base viewBox height
 * @param {number} rx - corner radius of the marker box
 * @param {boolean} isFlagged - whether to draw the red attention ring
 * @param {number} scale - render scale (used for hover highlight growth)
 */
const buildMarkerIcon = (inner, baseW, baseH, rx, isFlagged, scale = 1) => {
  const pad = isFlagged ? FLAG_RING : 0;
  const vbW = baseW + 2 * pad;
  const vbH = baseH + 2 * pad;
  const outW = Math.round(vbW * scale);
  const outH = Math.round(vbH * scale);

  const flagRect = isFlagged
    ? `<rect x="0" y="0" width="${vbW}" height="${vbH}" rx="${rx + pad}" fill="${FLAG_COLOR}"/>`
    : '';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${outH}" viewBox="0 0 ${vbW} ${vbH}">
      ${flagRect}
      <g transform="translate(${pad}, ${pad})">${inner}</g>
    </svg>
  `;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    width: outW,
    height: outH
  };
};

/**
 * Extract order number suffix from internal_order_number
 * e.g., "20260216-001" → "1", "20260216-014" → "14"
 */
export const getOrderNumberSuffix = (internalOrderNumber) => {
  if (!internalOrderNumber) return '?';
  const parts = internalOrderNumber.split('-');
  if (parts.length === 2) {
    return parseInt(parts[1], 10).toString();
  }
  return internalOrderNumber;
};

/**
 * Create a dual-info marker icon for stops in a run
 * Shows stop number (green) on top, order number (white) on bottom
 * @param {number} stopNumber - The sequence number in the run
 * @param {string} orderNumberSuffix - The order number suffix (e.g., "1", "14")
 * @param {boolean} isHighlighted - Whether the marker is highlighted (hovered)
 * @param {string} driverColor - Optional hex color for driver assignment (e.g., "#EF4444")
 * @param {boolean} isFlagged - Whether the order is flagged for attention
 */
export const createDualMarkerIcon = (stopNumber, orderNumberSuffix, isHighlighted = false, driverColor = null, isFlagged = false) => {
  // Use driver color if provided, otherwise default blue
  const defaultBgColor = '#1e3a5f';
  const bgColor = isHighlighted ? '#dc2626' : escapeHtml(driverColor || defaultBgColor);
  const stopColor = '#4ade80';
  const orderColor = '#ffffff';

  const inner = `
      <rect x="2" y="2" width="28" height="36" rx="4" fill="${bgColor}" stroke="white" stroke-width="2"/>
      <line x1="4" y1="20" x2="28" y2="20" stroke="white" stroke-width="1" opacity="0.3"/>
      <text x="16" y="14" text-anchor="middle" fill="${stopColor}" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${escapeHtml(stopNumber)}</text>
      <text x="16" y="32" text-anchor="middle" fill="${orderColor}" font-family="Arial, sans-serif" font-size="10" opacity="0.9">#${escapeHtml(orderNumberSuffix)}</text>
  `;

  return buildMarkerIcon(inner, 32, 40, 4, isFlagged, isHighlighted ? 1.1 : 1);
};

/**
 * Create marker icon for available orders (not in a run)
 * Amber square with order number only
 * Used for: 'accepted' status orders (awaiting design)
 */
export const createAvailableMarkerIcon = (orderNumberSuffix, isHighlighted = false, isFlagged = false) => {
  const bgColor = isHighlighted ? '#dc2626' : '#d97706';
  const inner = `
      <rect x="2" y="2" width="24" height="24" rx="4" fill="${bgColor}" stroke="white" stroke-width="2"/>
      <text x="14" y="18" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="11" font-weight="bold">#${escapeHtml(orderNumberSuffix)}</text>
  `;
  return buildMarkerIcon(inner, 28, 28, 4, isFlagged, isHighlighted ? 1.15 : 1);
};

/**
 * Create marker icon for designed orders (ready to be assigned)
 * Purple square with order number - indicates order is ready for delivery assignment
 * Used for: 'designed' status orders
 */
export const createDesignedMarkerIcon = (orderNumberSuffix, isHighlighted = false, isFlagged = false) => {
  const bgColor = isHighlighted ? '#dc2626' : '#7c3aed'; // Violet-600 for better visibility
  const inner = `
      <rect x="2" y="2" width="24" height="24" rx="4" fill="${bgColor}" stroke="white" stroke-width="2"/>
      <text x="14" y="18" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="11" font-weight="bold">#${escapeHtml(orderNumberSuffix)}</text>
  `;
  return buildMarkerIcon(inner, 28, 28, 4, isFlagged, isHighlighted ? 1.15 : 1);
};

/**
 * Create marker icon for awaiting design orders
 * Amber/Orange square with order number - indicates order is accepted but not yet designed
 * Used for: 'accepted' status orders (awaiting design)
 */
export const createAwaitingDesignMarkerIcon = (orderNumberSuffix, isHighlighted = false, isFlagged = false) => {
  const bgColor = isHighlighted ? '#dc2626' : '#f59e0b'; // Amber
  const inner = `
      <rect x="2" y="2" width="24" height="24" rx="4" fill="${bgColor}" stroke="white" stroke-width="2"/>
      <text x="14" y="18" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="11" font-weight="bold">#${escapeHtml(orderNumberSuffix)}</text>
  `;
  return buildMarkerIcon(inner, 28, 28, 4, isFlagged, isHighlighted ? 1.15 : 1);
};

/**
 * Create marker icon for committed orders (in another committed run, not yet delivered)
 * Darker grayed out square with order number - indicates order is taken and still en route
 */
export const createCommittedMarkerIcon = (orderNumberSuffix, isFlagged = false) => {
  const bgColor = '#6b7280'; // Gray-500 (darker)
  const inner = `
      <rect x="2" y="2" width="24" height="24" rx="4" fill="${bgColor}" stroke="#4b5563" stroke-width="2"/>
      <text x="14" y="18" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="11" font-weight="bold">#${escapeHtml(orderNumberSuffix)}</text>
  `;
  return buildMarkerIcon(inner, 28, 28, 4, isFlagged);
};

/**
 * Create marker icon for delivered orders (completed delivery)
 * Lighter gray square with order number - faded to reduce visual prominence
 */
export const createDeliveredMarkerIcon = (orderNumberSuffix, isFlagged = false) => {
  const bgColor = '#d1d5db'; // Gray-300 (lighter)
  const inner = `
      <rect x="2" y="2" width="24" height="24" rx="4" fill="${bgColor}" stroke="#9ca3af" stroke-width="2"/>
      <text x="14" y="18" text-anchor="middle" fill="#6b7280" font-family="Arial, sans-serif" font-size="11" font-weight="bold">#${escapeHtml(orderNumberSuffix)}</text>
  `;
  return buildMarkerIcon(inner, 28, 28, 4, isFlagged);
};
